import { useRef, useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ChevronDown, GripVertical, Images, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { SortableArea } from "@/components/admin/Sortable";
import { ColsPicker, Inline } from "@/components/admin/ve/VideosFields";
import { PhotosFields } from "@/components/admin/ve/PhotosFields";
import { fileUrl } from "@/lib/api";

const input = "w-full rounded-lg border border-[#14213D]/15 bg-white px-2.5 py-2 font-ui text-xs text-[#14213D] outline-none focus:border-[#C8862F]";

const Album = ({ a, i, t, open, onOpen, onChange, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: a.id });
  const cover = a.cover || a.photos?.[0]?.url;
  const at = `${t}-album-${i}`;
  return (
    <li ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} data-testid={at} className={`rounded-lg border bg-white ${isDragging ? "z-10 border-[#C8862F] shadow-lg" : "border-[#14213D]/10"}`}>
      <div className="flex items-center gap-2 p-2">
        <button type="button" data-testid={`${at}-drag`} {...attributes} {...listeners} aria-label="Drag to reorder" className="cursor-grab touch-none rounded p-0.5 text-[#5F6875] active:cursor-grabbing"><GripVertical className="h-4 w-4" /></button>
        {cover ? <img src={fileUrl(cover)} alt="" className="h-10 w-14 shrink-0 rounded object-cover" /> : <span className="grid h-10 w-14 shrink-0 place-items-center rounded bg-[#14213D]/5 text-[#5F6875]"><Images className="h-4 w-4" /></span>}
        <button type="button" data-testid={`${at}-open`} onClick={onOpen} className="flex min-w-0 flex-1 items-center gap-1 text-left">
          <span className="min-w-0 flex-1 truncate font-ui text-sm text-[#14213D]"><b>{a.title || "Untitled album"}</b> <span className="text-[#5F6875]">{(a.photos || []).length} photo(s)</span></span>
          <ChevronDown className={`h-4 w-4 shrink-0 text-[#5F6875] transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        <button type="button" data-testid={`${at}-delete`} onClick={onDelete} aria-label="Delete album" className="rounded-full p-1.5 text-[#B4442A] hover:bg-[#B4442A]/10"><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
      {open && (
        <div className="grid gap-2 border-t border-[#14213D]/10 p-2">
          <Inline testid={`${at}-title`} value={a.title} placeholder="Album name" onSave={(x) => onChange({ ...a, title: x })} />
          <PhotosFields noCols b={{ id: `${t.replace("ve-block-", "")}-${a.id}`, items: a.photos || [] }} cover={a.cover} onCover={(url) => onChange({ ...a, cover: url })}
            onChange={(nb) => onChange({ ...a, photos: nb.items, cover: nb.items.some((p) => p.url === a.cover) ? a.cover : "" })} />
        </div>
      )}
    </li>
  );
};

// Photo albums: create named albums, fill each with photos, choose a cover, and drag albums into order.
export const AlbumsFields = ({ b, onChange }) => {
  const [name, setName] = useState("");
  const [open, setOpen] = useState("");
  const latest = useRef(b);
  latest.current = b;
  const albums = Array.isArray(b.albums) ? b.albums : [];
  const save = (list, extra = {}) => onChange({ ...latest.current, albums: list, ...extra });
  const current = () => (Array.isArray(latest.current.albums) ? latest.current.albums : []);
  const add = () => {
    const title = name.trim();
    if (!title) return toast.error("Type a name for the album first, like \"Easter 2026\".");
    const a = { id: `a-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`, title, cover: "", photos: [] };
    save([...albums, a]);
    setName("");
    setOpen(a.id);
    return toast.success("Album created. Now upload its photos.");
  };
  const t = `ve-block-${b.id}`;
  return (
    <div className="grid gap-3">
      <ColsPicker value={b.cols || 4} onPick={(n) => save(albums, { cols: n })} t={t} noun="Albums" />
      <div className="flex gap-2">
        <input data-testid={`${t}-new-album-name`} value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="New album name, e.g. Easter 2026" className={input} />
        <button type="button" data-testid={`${t}-new-album-add`} onClick={add} className="flex shrink-0 items-center gap-1 rounded-lg px-3 font-ui text-xs font-semibold" style={{ backgroundColor: "#2563EB", color: "#FFFFFF" }}><Plus className="h-3.5 w-3.5" /> Create</button>
      </div>
      {albums.length > 0 && <p className="font-ui text-[11px] text-[#5F6875]">{albums.length} album(s). Click one to add photos and pick its cover. Albums without photos stay hidden on the website.</p>}
      <SortableArea ids={albums.map((a) => a.id)} onReorder={(ids) => save(ids.map((id) => albums.find((a) => a.id === id)))}>
        <ul className="grid gap-2">
          {albums.map((a, i) => (
            <Album key={a.id} a={a} i={i} t={t} open={open === a.id} onOpen={() => setOpen(open === a.id ? "" : a.id)}
              onChange={(na) => save(current().map((x) => (x.id === a.id ? na : x)))}
              onDelete={() => window.confirm(`Delete the album "${a.title || "Untitled"}" and its photos?`) && save(current().filter((x) => x.id !== a.id))} />
          ))}
        </ul>
      </SortableArea>
    </div>
  );
};
